import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { config } from '../config/env';
import './Integrations.css';
import { Search, ExternalLink, BookOpen, MessageSquare, Check, X, Code, Activity } from 'lucide-react';
import Dialog from '../components/Dialog';

// ... (imports)

const Integrations = () => {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');
    const [linearStatus, setLinearStatus] = useState({ connected: false, loading: true });
    const [jiraStatus, setJiraStatus] = useState({ connected: false, loading: true });
    const [githubStatus, setGithubStatus] = useState({ connected: false, loading: true });
    const [slackStatus, setSlackStatus] = useState({ connected: false, loading: true });
    const [observabilityStatus, setObservabilityStatus] = useState({ connected: false, loading: true });
    const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
    const [disconnectTarget, setDisconnectTarget] = useState(null); // 'linear' or 'jira'

    useEffect(() => {
        if (currentWorkspace) {
            checkLinearStatus();
            checkJiraStatus();
            checkGithubStatus();
            checkSlackStatus();
            checkObservabilityStatus();
        }
    }, [currentWorkspace]);

    const checkLinearStatus = async () => {
        try {
            const response = await api.get('/integrations/linear/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            setLinearStatus({ ...response.data, loading: false });
        } catch (error) {
            console.error('Error checking Linear status:', error);
            setLinearStatus({ connected: false, loading: false });
        }
    };

    const checkJiraStatus = async () => {
        try {
            const response = await api.get('/integrations/jira/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            setJiraStatus({ ...response.data, loading: false });
        } catch (error) {
            console.error('Error checking Jira status:', error);
            setJiraStatus({ connected: false, loading: false });
        }
    };

    const checkGithubStatus = async () => {
        try {
            const response = await api.get('/integrations/github/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            setGithubStatus({ ...response.data, loading: false });
        } catch (error) {
            console.error('Error checking GitHub status:', error);
            setGithubStatus({ connected: false, loading: false });
        }
    };

    const checkSlackStatus = async () => {
        try {
            const response = await api.get('/integrations/slack/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            setSlackStatus({ ...response.data, loading: false });
        } catch (error) {
            console.error('Error checking Slack status:', error);
            setSlackStatus({ connected: false, loading: false });
        }
    };

    const checkObservabilityStatus = async () => {
        try {
            const response = await api.get('/integrations/observability/status', {
                params: { workspaceId: currentWorkspace.id }
            });
            setObservabilityStatus({ ...response.data, loading: false });
        } catch (error) {
            console.error('Error checking Observability status:', error);
            setObservabilityStatus({ connected: false, loading: false });
        }
    };

    const handleConnect = async (provider) => {
        try {
            const response = await api.get(`/integrations/${provider}/authorize`, {
                params: {
                    workspaceId: currentWorkspace.id,
                    userId: user?.id
                }
            });

            if (response.data.url) {
                window.location.href = response.data.url;
            } else {
                throw new Error('No authorization URL returned from backend');
            }
        } catch (error) {
            console.error(`Error connecting to ${provider}:`, error);
            alert(`Failed to start ${provider} connection flow. Please try again.`);
        }
    };

    const handleDisconnect = (target) => {
        setDisconnectTarget(target);
        setDisconnectDialogOpen(true);
    };

    const confirmDisconnect = async () => {
        try {
            if (disconnectTarget === 'linear') {
                await api.delete('/integrations/linear/disconnect', {
                    data: { workspaceId: currentWorkspace.id }
                });
                setLinearStatus({ connected: false, loading: false });
            } else if (disconnectTarget === 'jira') {
                await api.delete('/integrations/jira/disconnect', {
                    data: { workspaceId: currentWorkspace.id }
                });
                setJiraStatus({ connected: false, loading: false });
            } else if (disconnectTarget === 'github') {
                await api.delete('/integrations/github/disconnect', {
                    data: { workspaceId: currentWorkspace.id }
                });
                setGithubStatus({ connected: false, loading: false });
            } else if (disconnectTarget === 'slack') {
                await api.delete('/integrations/slack/disconnect', {
                    data: { workspaceId: currentWorkspace.id }
                });
                setSlackStatus({ connected: false, loading: false });
            } else if (disconnectTarget === 'observability') {
                await api.delete('/integrations/observability/disconnect', {
                    data: { workspaceId: currentWorkspace.id }
                });
                setObservabilityStatus({ connected: false, loading: false });
            }
        } catch (error) {
            console.error(`Error disconnecting ${disconnectTarget}:`, error);
            alert(`Failed to disconnect ${disconnectTarget}`);
        } finally {
            setDisconnectDialogOpen(false);
            setDisconnectTarget(null);
        }
    };

    // Definition of all integrations
    const integrationsList = [
        {
            id: 'linear',
            name: 'Linear',
            description: 'Sync test results and performance regressions with your engineering backlog for faster resolution.',
            category: 'Task Management',
            icon: 'linear',
            connected: linearStatus.connected,
            loading: linearStatus.loading,
            action: linearStatus.connected ? () => window.location.href = '/integrations/linear' : () => handleConnect('linear'),
            disconnectAction: () => handleDisconnect('linear'),
            enabled: true
        },
        {
            id: 'jira',
            name: 'Jira',
            description: 'Task Rova to run tests directly from Jira tickets by commenting "@rova" or "Rova:".',
            category: 'Task Management',
            icon: 'jira',
            connected: jiraStatus.connected,
            loading: jiraStatus.loading,
            action: jiraStatus.connected ? () => window.location.href = '/integrations/jira' : () => handleConnect('jira'),
            disconnectAction: () => handleDisconnect('jira'),
            enabled: false
        },
        {
            id: 'slack',
            name: 'Slack',
            description: 'Get real-time test execution alerts and detailed crash reports directly in your Slack channels.',
            category: 'Communication',
            icon: 'slack',
            status: slackStatus.connected ? 'connected' : 'disconnected',
            connected: slackStatus.connected,
            loading: slackStatus.loading,
            action: slackStatus.connected ? () => window.location.href = '/integrations/slack' : () => handleConnect('slack'),
            disconnectAction: () => handleDisconnect('slack'),
            enabled: config.ENABLE_SLACK
        },
        {
            id: 'github',
            name: 'GitHub',
            description: 'Automatically trigger tests on pull requests and get test results as PR comments.',
            category: 'DevOps',
            icon: 'github',
            status: githubStatus.connected ? 'connected' : 'disconnected',
            connected: githubStatus.connected,
            loading: githubStatus.loading,
            action: () => handleConnect('github'),
            disconnectAction: () => handleDisconnect('github'),
            enabled: config.ENABLE_GITHUB
        },
        // ... (other integrations)
        {
            id: 'teams',
            name: 'Microsoft Teams',
            description: 'Collaborate on test results and receive health snapshots within your Teams workspace.',
            category: 'Communication',
            icon: 'teams',
            connected: false,
            enabled: false
        },
        {
            id: 'asana',
            name: 'Asana',
            description: 'Manage testing tasks, assignee flows, and bug tracking directly within Asana projects.',
            category: 'Task Management',
            icon: 'asana',
            connected: false,
            enabled: false
        },
        {
            id: 'testpod',
            name: 'TestPod',
            description: 'Sync autonomous AI test cases and results with your professional TCM suite for unified reporting.',
            category: 'TCM',
            icon: 'testpod',
            connected: false,
            enabled: false
        },
        {
            id: 'testrail',
            name: 'TestRail',
            description: 'Sync autonomous AI test cases and results with your professional TCM suite for unified reporting.',
            category: 'TCM',
            icon: 'testrail',
            connected: false,
            enabled: false
        },
        {
            id: 'observability',
            name: 'Observability & Monitoring',
            description: 'Link Grafana/Kibana dashboards and export test execution traces via OpenTelemetry OTLP.',
            category: 'Observability',
            icon: 'observability',
            connected: observabilityStatus.connected,
            loading: observabilityStatus.loading,
            action: () => window.location.href = '/integrations/observability',
            disconnectAction: () => handleDisconnect('observability'),
            enabled: true
        }
    ];

    // Filter logic
    const filteredIntegrations = integrationsList.filter(item => {
        const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = activeFilter === 'All' || item.category === activeFilter;
        return matchesSearch && matchesFilter;
    });

    const filters = ['All', 'Communication', 'Task Management', 'TCM', 'DevOps', 'Observability'];

    return (
        <div className="integrations-page">
            <div className="integrations-header-section">
                <div className="header-top">
                    <div>
                        <h1>Integrations</h1>
                        <p className="subtitle">Connect your mobile testing suite with your existing engineering stack to automate workflows and sync bug reports.</p>
                    </div>
                    <button className="btn-secondary flex-center gap-2">
                        <BookOpen size={16} /> Documentation
                    </button>
                </div>

                <div className="search-bar-container">
                    <Search className="search-icon" size={20} />
                    <input
                        type="text"
                        placeholder="Search integrations (e.g., Jira, Slack, TestRail...)"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="filter-chips">
                    {filters.map(filter => (
                        <button
                            key={filter}
                            className={`filter-chip ${activeFilter === filter ? 'active' : ''}`}
                            onClick={() => setActiveFilter(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>

            <div className="integrations-grid-layout">
                {filteredIntegrations.map(integration => (
                    <div key={integration.id} className={`marketplace-card ${!integration.enabled ? 'coming-soon' : ''}`}>
                        {!integration.enabled && (
                            <div className="coming-soon-overlay">
                                <span>Coming Soon</span>
                            </div>
                        )}
                        <div className="card-header">
                            <div className={`app-icon ${integration.id}`}>
                                {/* Placeholder icons processing */}
                                {integration.id === 'linear' && <svg viewBox="0 0 32 32" fill="none"><path d="M16 8L24 16L16 24L8 16L16 8Z" fill="white" /></svg>}
                                {integration.id === 'slack' && <span className="logo-text">Slack</span>}
                                {integration.id === 'jira' && <span className="logo-text">Jira</span>}
                                {integration.id === 'github' && <svg viewBox="0 0 24 24" width="24" height="24" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path></svg>}
                                {integration.id === 'teams' && <span className="logo-placeholder">MT</span>}
                                {integration.id === 'asana' && <span className="logo-text">Asana</span>}
                                {integration.id === 'testrail' && <span className="logo-placeholder">TR</span>}
                                {integration.id === 'testpod' && <span className="logo-text">TestPod</span>}
                                {integration.id === 'observability' && <Activity size={24} color="white" />}
                            </div>
                            {integration.connected && (
                                <div className="integrations__status-badge active">
                                    <span className="dot"></span> ACTIVE
                                </div>
                            )}
                        </div>

                        <h3>{integration.name}</h3>
                        <p>{integration.description}</p>

                        <div className="card-footer">
                            {integration.connected ? (
                                <div className="connected-actions">
                                    <button
                                        className="btn-configure"
                                        onClick={integration.action || (() => { })}
                                    >
                                        <Code size={16} /> Configure
                                    </button>
                                    <button
                                        className="btn-disconnect"
                                        onClick={integration.disconnectAction || (() => { })}
                                        title="Disconnect Integration"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="btn-connect full-width"
                                    onClick={integration.action || (() => { })}
                                    disabled={!integration.enabled}
                                >
                                    Connect
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="request-banner">
                <div className="banner-content">
                    <h3>Can't find the tool you use?</h3>
                    <p>We're constantly adding new integrations. Request a new one or build your own using our API.</p>
                </div>
                <div className="banner-actions">
                    <button className="btn-primary-purple">Request Integration</button>
                    <button className="btn-secondary-dark">Developer API</button>
                </div>
            </div>

            <Dialog
                isOpen={disconnectDialogOpen}
                onClose={() => setDisconnectDialogOpen(false)}
                title={`Disconnect ${disconnectTarget?.charAt(0).toUpperCase() + disconnectTarget?.slice(1)} Integration`}
                message={`Are you sure you want to disconnect ${disconnectTarget}? All project mappings will be deactivated. This action cannot be undone.`}
                type="confirm"
                variant="danger"
                confirmText="Disconnect"
                cancelText="Cancel"
                onConfirm={confirmDisconnect}
            />
        </div>
    );
};

export default Integrations;
